import { StreamProcessorSrc } from "./worklets/stream_processor.js";
import { AudioAnalysis } from "./analysis/audio_analysis.js";

/**
 * Plays audio streams received in raw PCM16 chunks from the browser
 * @class
 */
export class WavStreamPlayer {
  constructor({ sampleRate = 44100 } = {}) {
    this.scriptSrc = StreamProcessorSrc;
    this.sampleRate = sampleRate;
    this.context = null;
    this.stream = null;
    this.fileSource = null; // Reference to the file playback source
    this.analyser = null;
    this.trackSampleOffsets = {};
    this.interruptedTrackIds = {};
  }

  async connect() {
    if (!this.context) {
      this.context = new AudioContext({ sampleRate: this.sampleRate });
      if (this.context.state === "suspended") {
        await this.context.resume();
      }
      try {
        await this.context.audioWorklet.addModule(this.scriptSrc);
      } catch (e) {
        console.error(e);
        throw new Error(`Could not add audioWorklet module: ${this.scriptSrc}`);
      }
      this.analyser = this.context.createAnalyser();
      this.analyser.fftSize = 8192;
      this.analyser.smoothingTimeConstant = 0.1;
    }
    return true;
  }

  /**
   * Stops any currently playing stream or file
   */
  stopAll() {
    if (this.stream) {
      this.stream.disconnect();
      this.stream = null;
    }
    if (this.fileSource) {
      this.fileSource.stop();
      this.fileSource.disconnect();
      this.fileSource = null;
    }
  }

  getFrequencies(
    analysisType = "frequency",
    minDecibels = -100,
    maxDecibels = -30
  ) {
    if (!this.analyser) {
      throw new Error("Not connected, please call .connect() first");
    }
    return AudioAnalysis.getFrequencies(
      this.analyser,
      this.sampleRate,
      null,
      analysisType,
      minDecibels,
      maxDecibels
    );
  }

  async _start() {
    this.stopAll(); // Stop any active audio source
    const streamNode = new AudioWorkletNode(this.context, "stream_processor");
    streamNode.connect(this.context.destination);
    streamNode.port.onmessage = (e) => {
      const { event } = e.data;
      if (event === "stop") {
        streamNode.disconnect();
        this.stream = null;
      } else if (event === "offset") {
        const { requestId, trackId, offset } = e.data;
        const currentTime = offset / this.sampleRate;
        this.trackSampleOffsets[requestId] = { trackId, offset, currentTime };
      }
    };
    this.analyser.disconnect();
    streamNode.connect(this.analyser);
    this.stream = streamNode;
    return true;
  }

  /**
   * Adds 16BitPCM data to the currently playing audio stream
   * @param {ArrayBuffer|Int16Array} arrayBuffer
   * @param {string} [trackId]
   * @returns {Int16Array}
   */
  async add16BitPCM(arrayBuffer, trackId = "default") {
    if (typeof trackId !== "string") {
      throw new Error(`trackId must be a string`);
    } else if (this.interruptedTrackIds[trackId]) {
      return;
    }

    if (!this.stream) {
      await this._start(); // Ensure the stream is started
    }

    if (!this.stream || !this.stream.port) {
      throw new Error("Stream is not initialized. Please check the setup.");
    }

    let buffer;
    if (arrayBuffer instanceof Int16Array) {
      buffer = arrayBuffer;
    } else if (arrayBuffer instanceof ArrayBuffer) {
      buffer = new Int16Array(arrayBuffer);
    } else {
      throw new Error(`Argument must be Int16Array or ArrayBuffer`);
    }

    this.stream.port.postMessage({ event: "write", buffer, trackId });
    return buffer;
  }
  async playFile(file) {
    this.stopAll(); // Stop any active audio source
    if (!this.context) {
      await this.connect();
    }

    let arrayBuffer;

    if (file instanceof File || file instanceof Blob) {
      arrayBuffer = await file.arrayBuffer();
    } else if (file instanceof ArrayBuffer) {
      arrayBuffer = file;
    } else if (typeof file === "string") {
      const response = await fetch(file);
      arrayBuffer = await response.arrayBuffer();
    } else {
      throw new Error(
        "Unsupported file type. Must be File, Blob, ArrayBuffer, or URL string."
      );
    }

    const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
    const source = this.context.createBufferSource();
    source.buffer = audioBuffer;

    source.connect(this.analyser);
    this.analyser.connect(this.context.destination);

    this.fileSource = source; // Save reference to the file playback source

    source.start();

    source.onended = () => {
      source.disconnect();
      if (this.fileSource === source) {
        this.fileSource = null;
      }
    };

    return true;
  }

  /**
   * Gets the offset (sample count) of the currently playing stream
   * @param {boolean} [interrupt]
   * @returns {{trackId: string|null, offset: number, currentTime: number}}
   */

  async getTrackSampleOffset(interrupt = false) {
    if (!this.stream) {
      return null;
    }
    const requestId = crypto.randomUUID();
    this.stream.port.postMessage({
      event: interrupt ? "interrupt" : "offset",
      requestId,
    });
    let trackSampleOffset;
    while (!trackSampleOffset) {
      trackSampleOffset = this.trackSampleOffsets[requestId];
      await new Promise((r) => setTimeout(() => r(), 1));
    }
    const { trackId } = trackSampleOffset;
    if (interrupt && trackId) {
      this.interruptedTrackIds[trackId] = true;
    }
  }

  /**
   * Strips the current stream and returns the sample offset of the audio
   * @param {boolean} [interrupt]
   * @returns {{trackId: string|null, offset: number, currentTime: number}}
   */
  async interrupt() {
    return this.getTrackSampleOffset(true);
  }
}

globalThis.WavStreamPlayer = WavStreamPlayer;
